import {
  Component,
  useEffect,
  useState,
  type CSSProperties,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { color, radius } from '@cwos/design-tokens';
import { reportClientError, subscribeClientErrors, type ClientError } from './errorReporting.js';

/**
 * The last line of defence for Admin Web.
 *
 * React unmounts the whole tree when a render throws. Without a boundary the
 * result is a blank white page: no message, no reload button, no evidence
 * anything happened. Someone looking at it cannot tell a crash from a slow
 * network from their own mistake, and support cannot either.
 *
 * Deliberately dependency-free and deliberately outside every provider, so it
 * still works when the failure is the router, the query client, or auth itself.
 * For the same reason the fallback leans on inline styles with token fallbacks
 * rather than on component library markup it might not be able to render.
 *
 * THE FALLBACK HALF OF EVERY `var()` IS THE TOKEN, not a hex copied out of it.
 * The two-value form is load-bearing here — this screen has to draw when
 * tokens.css is the thing that failed to load — but a hex typed into the second
 * slot is a colour that stops moving when the token moves, and `--color-look-*`
 * has already been retuned once for contrast. Reading `color` from
 * `@cwos/design-tokens` keeps the resilience and removes the copy: the fallback
 * is the token's value at build time, so a retune reaches this file too. The
 * import is a plain object of strings with no runtime behaviour, so it cannot
 * be the thing that throws.
 */

interface Props {
  children: ReactNode;
  /** What failed, for the copy: "the schedule" reads better than "the app". */
  what?: string;
}

interface State {
  error: Error | null;
  /**
   * The reference the user can quote. It is the id the report was sent under,
   * which is the API's request id for it, which is what an operator searches
   * Cloud Logging for. Without it "it broke" is unsearchable — which is what a
   * client-side crash used to be.
   */
  reference: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, reference: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, reference: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // `info.componentStack` deliberately stays out of the record: it is long,
    // and it is not what the person in front of the screen needs. The record
    // exists so the failure is not silent.
    void info;
    const reported = reportClientError('render', error);
    this.setState({ reference: reported.id });
  }

  private reset = () => this.setState({ error: null, reference: null });

  override render(): ReactNode {
    const { error, reference } = this.state;
    if (!error) return this.props.children;
    return (
      <CrashFallback
        error={error}
        reference={reference}
        what={this.props.what}
        onRetry={this.reset}
      />
    );
  }
}

const cardStyle: CSSProperties = {
  maxWidth: 520,
  margin: '10vh auto',
  padding: 24,
  background: `var(--color-surface, ${color.surface})`,
  border: `1px solid var(--color-line, ${color.line})`,
  borderRadius: `var(--radius-md, ${radius.md})`,
  color: `var(--color-ink, ${color.ink})`,
  font: 'inherit',
};

const buttonStyle: CSSProperties = {
  padding: '10px 16px',
  minHeight: 36,
  background: 'transparent',
  color: `var(--color-ink2, ${color.ink2})`,
  border: `1px solid var(--color-line, ${color.line})`,
  borderRadius: `var(--radius-sm, ${radius.sm})`,
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
};

function CrashFallback({
  error,
  reference,
  what,
  onRetry,
}: {
  error: Error;
  reference: string | null;
  what?: string;
  onRetry: () => void;
}) {
  return (
    <main style={cardStyle} role="alert">
      <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>
        {what ? `${what} stopped working` : 'Phibian ran into a problem'}
      </h1>
      <p style={{ margin: '0 0 8px', color: `var(--color-ink2, ${color.ink2})` }}>
        Something on this screen failed while it was drawing. Nothing you had already saved is
        affected — this went wrong in your browser, not in your organization’s records.
      </p>
      <p style={{ margin: '0 0 8px', color: `var(--color-soft, ${color.soft})`, fontSize: 12.5 }}>
        Details, in case you are reporting it: <code>{error.message || error.name}</code>
      </p>
      {reference ? (
        <p
          style={{ margin: '0 0 16px', color: `var(--color-soft, ${color.soft})`, fontSize: 12.5 }}
        >
          Reference: <code>{reference}</code> — quoting this lets us find the exact record of what
          happened.
        </p>
      ) : (
        <div style={{ height: 8 }} />
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" style={buttonStyle} onClick={onRetry}>
          Try this screen again
        </button>
        <button type="button" style={buttonStyle} onClick={() => window.location.reload()}>
          Reload Phibian
        </button>
      </div>
    </main>
  );
}

const noticeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 16px',
  background: `var(--color-look-soft, ${color.look.soft})`,
  color: `var(--color-look-deep, ${color.look.deep})`,
  borderBottom: `1px solid var(--color-line, ${color.line})`,
  fontSize: 13,
};

/**
 * What an error OUTSIDE the render path looks like: a failure on a timer, in an
 * event handler, or a promise nobody handled. Those do not unmount anything, so
 * the boundary above never sees them — and the old behaviour was that the
 * screen simply stopped responding with no explanation at all.
 *
 * A strip, not a modal: the app underneath is very often still usable, and
 * blocking it would be a bigger lie than staying quiet was.
 */
export function GlobalErrorNotice() {
  const [error, setError] = useState<ClientError | null>(null);

  useEffect(
    () =>
      subscribeClientErrors((next) => {
        // A render failure is the boundary's to explain — it has already
        // replaced this strip with a full screen that says more.
        if (next.source !== 'render') setError(next);
      }),
    [],
  );

  if (!error) return null;
  return (
    <div style={noticeStyle} role="status">
      <span>
        Something didn’t finish just now ({error.message}). If this screen looks wrong, reloading
        will put it right. Reference <code>{error.id}</code>.
      </span>
      <button
        type="button"
        style={{ ...buttonStyle, padding: '4px 10px', minHeight: 0 }}
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
      <button
        type="button"
        style={{ ...buttonStyle, padding: '4px 10px', minHeight: 0, border: 'none' }}
        onClick={() => setError(null)}
      >
        Dismiss
      </button>
    </div>
  );
}
